import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    TextField,
    Button,
    Typography,
    Alert,
    CircularProgress
} from '@mui/material';
import { styled } from '@mui/system';
import { useAuth } from '../../context/AuthContext';
import AppColors from '../../utils/AppColors';

// Use the same pattern as vendor login
const patternImage = '/Pattern.png';

const Background = styled('div')({
    minHeight: '100vh',
    width: '100vw',
    position: 'fixed',
    top: 0,
    left: 0,
    zIndex: 0,
    backgroundColor: AppColors.primaryDark,
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        backgroundImage: `url(${patternImage})`,
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        opacity: 0.3,
        zIndex: 1,
    },
    '&:after': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: `radial-gradient(circle at 0 0, transparent 0%, ${AppColors.primaryDark}90 20%, ${AppColors.primaryDark} 50%)`,
        zIndex: 2,
    }
});

const CenteredContainer = styled('div')({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    zIndex: 3,
});

const LoginCard = styled('div')({
    width: '100%',
    maxWidth: 520,
    background: AppColors.backgroundWhite,
    borderRadius: 24,
    boxShadow: '0 8px 32px rgba(0,0,0,0.16)',
    padding: '3rem 2.5rem 2.5rem 2.5rem',
    textAlign: 'center',
    position: 'relative',
    zIndex: 3,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
});

const Logo = styled('img')({
    width: 160,
    marginBottom: '2rem',
    marginTop: '-1rem',
});

const Title = styled('h2')({
    fontSize: '2rem',
    color: AppColors.primaryDark,
    marginBottom: '0.5rem',
    fontWeight: 700,
    letterSpacing: 0.5,
});

const Subtitle = styled('p')({
    color: AppColors.textSecondary,
    marginBottom: '2rem',
    fontSize: '1.1rem',
});

const StyledForm = styled('form')({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: '1.2rem',
});

const StyledTextField = styled(TextField)({
    width: '100%',
    '& .MuiOutlinedInput-root': {
        borderRadius: 12,
        backgroundColor: AppColors.backgroundLight,
        fontSize: '1rem',
        '& fieldset': {
            borderColor: 'rgba(0,0,0,0.1)',
        },
        '&:hover fieldset': {
            borderColor: AppColors.primaryYellow,
        },
        '&.Mui-focused fieldset': {
            borderColor: AppColors.primaryYellow,
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': {
        color: AppColors.textSecondary,
        '&.Mui-focused': {
            color: AppColors.primaryYellow,
        },
    },
});

const LoginButton = styled(Button)({
    width: '100%',
    borderRadius: 64,
    padding: '1rem',
    fontSize: '1.1rem',
    fontWeight: 600,
    textTransform: 'none',
    backgroundColor: AppColors.primaryDark,
    color: AppColors.backgroundWhite,
    boxShadow: '0 2px 8px rgba(33,30,30,0.08)',
    marginTop: '0.5rem',
    '&:hover': {
        backgroundColor: AppColors.primaryYellow,
        color: AppColors.primaryDark,
    },
    '&:disabled': {
        opacity: 0.7,
        cursor: 'not-allowed',
    },
    transition: 'background 0.2s',
});

const Login = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const { login } = useAuth();
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        const result = await login(email, password);
        
        if (result.success) {
            navigate('/dashboard');
        } else {
            setError(result.error);
        }
        
        setLoading(false);
    };

    return (
        <>
            <Background />
            <CenteredContainer>
                <LoginCard>
                    <Logo src="/jaddid-logo.png" alt="Jaddid Admin" />
                    <Title>Admin Portal</Title>
                    <Subtitle>Comprehensive management dashboard</Subtitle>
                    
                    {error && (
                        <Alert severity="error" sx={{ mb: 2, width: '100%', borderRadius: '12px' }}>
                            {error}
                        </Alert>
                    )}
                    
                    <StyledForm onSubmit={handleSubmit}>
                        <StyledTextField
                            label="Admin Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="email"
                        />
                        
                        <StyledTextField
                            label="Password"
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            autoComplete="current-password"
                        />
                        
                        <LoginButton
                            type="submit"
                            variant="contained"
                            disabled={loading}
                        >
                            {loading ? <CircularProgress size={24} color="inherit" /> : 'Access Admin Portal'}
                        </LoginButton>
                    </StyledForm>
                </LoginCard>
            </CenteredContainer>
        </>
    );
};

export default Login;
